// shading.ts — the viewer's §16 shading modes over an OSPL v2 cloud:
// 'captured' (the colours as stored, i.e. as the room lit them), 'albedo'
// (linear albedo shown in sRGB) and 'neutral' (albedo · ao under a white
// light whose luminance matches the scan's fitted SH-2 irradiance at each
// gaussian's normal). Also the ambient chip text (colour temperature and key
// light direction). The SH convention matches server/refine: `sh` holds 9
// IRRADIANCE coefficients × RGB, sh[j*3+c], real SH basis below, E ≥ 0.

import type { SplatCloud } from './format';

// Lighting is the slice of api.Lighting used here (declared locally so the
// splat component stays free of the api dependency).
export interface Lighting {
  sh: number[];
  ambient_k?: number;
  key_dir?: [number, number, number];
  key_strength?: number;
}

export type ShadingMode = 'captured' | 'neutral' | 'albedo';
export const SHADING_MODES: readonly ShadingMode[] = ['captured', 'neutral', 'albedo'];

// shBasis fills the 9 real SH-2 basis values at unit normal (x, y, z).
export function shBasis(x: number, y: number, z: number, out: Float64Array = new Float64Array(9)): Float64Array {
  out[0] = 0.282095;
  out[1] = 0.488603 * y;
  out[2] = 0.488603 * z;
  out[3] = 0.488603 * x;
  out[4] = 1.092548 * x * y;
  out[5] = 1.092548 * y * z;
  out[6] = 0.315392 * (3 * z * z - 1);
  out[7] = 1.092548 * x * z;
  out[8] = 0.546274 * (x * x - y * y);
  return out;
}

// irradiance is E(n) per channel from the 27 coefficients, clamped ≥ 0.
export function irradiance(sh: readonly number[], x: number, y: number, z: number): [number, number, number] {
  const b = shBasis(x, y, z);
  const e: [number, number, number] = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    let s = 0;
    for (let j = 0; j < 9; j++) s += (sh[j * 3 + c] ?? 0) * b[j];
    e[c] = Math.max(0, s);
  }
  return e;
}

// luminance is Rec. 709 Y of a linear RGB triple.
export function luminance(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// linearToSrgb is the sRGB OETF on 0..1 (clamped).
export function linearToSrgb(v: number): number {
  const x = Math.min(1, Math.max(0, v));
  return x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
}

// usableSH is true when lighting carries a full 27-coefficient SH-2 set.
function usableSH(lighting: Lighting | null | undefined): lighting is Lighting {
  return !!lighting && Array.isArray(lighting.sh) && lighting.sh.length >= 27;
}

// shadeCloud returns c with its colours replaced for `mode` (c itself for
// 'captured' or a cloud without material). Positions etc. are shared.
export function shadeCloud(c: SplatCloud, mode: ShadingMode, lighting?: Lighting | null): SplatCloud {
  const m = c.material;
  if (mode === 'captured' || !m) return c;
  const color = new Float32Array(c.count * 3);
  const sh = usableSH(lighting) ? lighting.sh : null;
  if (mode === 'neutral' && !sh) console.info('[openscan/shading] neutral without scan lighting: albedo · ao');
  for (let i = 0; i < c.count; i++) {
    let k = 1;
    if (mode === 'neutral') {
      k = m.ao[i];
      if (sh) {
        const e = irradiance(sh, m.normal[i * 3], m.normal[i * 3 + 1], m.normal[i * 3 + 2]);
        k *= luminance(e[0], e[1], e[2]);
      }
    }
    for (let ch = 0; ch < 3; ch++) color[i * 3 + ch] = linearToSrgb(m.albedo[i * 3 + ch] * k);
  }
  return { ...c, color };
}

// keyLightPhrase words key_dir (world, Z up) as "upper left" etc.
export function keyLightPhrase(d: readonly number[]): string {
  const [x, y, z] = d;
  const vert = z >= 0 ? 'upper' : 'lower';
  const horiz = Math.abs(x) >= Math.abs(y) ? (x >= 0 ? 'right' : 'left') : y >= 0 ? 'back' : 'front';
  return `${vert} ${horiz}`;
}

// ambientChip is the viewer's lighting summary, '' without lighting.
export function ambientChip(lighting: Lighting | null | undefined): string {
  if (!lighting) return '';
  const parts: string[] = [];
  if (typeof lighting.ambient_k === 'number' && lighting.ambient_k > 0) parts.push(`Ambient: ~${Math.round(lighting.ambient_k / 100) * 100} K`);
  if (lighting.key_dir && (lighting.key_strength ?? 0) > 0.15) parts.push(`key light from ${keyLightPhrase(lighting.key_dir)}`);
  return parts.join(', ');
}
